import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import { CYCLE_SCOPE_LABEL } from "@/utils/cycleScope";
import { PLAN_LABEL, subscriptionState, SUBSCRIPTION_WARNING_DAYS } from "@/utils/plans";
import type { School } from "@/types";

interface SchoolCardProps {
  school: School;
  /** Absent : l'utilisateur ne peut pas modifier les écoles. */
  onConfigure?: (school: School) => void;
  onToggleActive?: (school: School) => void;
}

/** Carte d'une école dans la console : identité et totaux (aucune donnée nominative). */
export default function SchoolCard({ school, onConfigure, onToggleActive }: SchoolCardProps) {
  const subscription = subscriptionState(school.subscriptionEndsAt);
  const endsAt = school.subscriptionEndsAt ? new Date(`${school.subscriptionEndsAt}T00:00:00`).toLocaleDateString("fr-FR") : "";
  return (
    <div className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-background-300">
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 flex items-center justify-center">
          {school.logo ? (
            <img src={school.logo} alt={school.name} title={school.name} className="h-full w-full object-cover" />
          ) : (
            <i className="ri-building-2-line text-xl text-foreground-500"></i>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold font-heading text-foreground-950">{school.name}</h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-foreground-500">
            <span className="truncate">{school.code}</span>
            {school.city && (
              <>
                <span className="text-foreground-300">•</span>
                <span className="truncate">{school.city}</span>
              </>
            )}
          </p>
        </div>
        <Badge tone={school.isActive ? "success" : "neutral"}>{school.isActive ? "Active" : "Inactive"}</Badge>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <Badge tone="primary" icon="ri-vip-crown-line">
          {PLAN_LABEL[school.plan]}
        </Badge>
        {school.subscriptionEndsAt && (
          <Badge
            tone={
              subscription.expired
                ? "danger"
                : (subscription.daysLeft ?? Infinity) <= SUBSCRIPTION_WARNING_DAYS
                  ? "warning"
                  : "neutral"
            }
            icon="ri-time-line"
          >
            {subscription.expired ? `Échu le ${endsAt}` : `Jusqu'au ${endsAt}`}
          </Badge>
        )}
        {school.cycles.map((c) => (
          <Badge key={c} tone="secondary" icon="ri-graduation-cap-line">
            {CYCLE_SCOPE_LABEL[c]}
          </Badge>
        ))}
        {school.schoolYear && (
          <Badge tone="neutral" icon="ri-calendar-line">
            {school.schoolYear}
          </Badge>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-background-200 pt-4">
        {[
          { label: "Élèves", value: school.studentsCount },
          { label: "Personnel", value: school.staffCount },
          { label: "Comptes", value: school.usersCount },
        ].map((item) => (
          <div key={item.label}>
            <p className="text-xs uppercase tracking-wide text-foreground-400">{item.label}</p>
            <p className="mt-0.5 text-base font-semibold font-heading text-foreground-900">
              {item.value.toLocaleString("fr-FR")}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-3 text-xs text-foreground-500 truncate">
        <i className="ri-calendar-check-line mr-1"></i>
        Ouverte le {new Date(school.createdAt).toLocaleDateString("fr-FR")}
      </div>

      {(onConfigure || onToggleActive) && (
        <div className="mt-auto flex items-center gap-2 pt-4">
          {onConfigure && (
            <Button size="sm" variant="outline" icon="ri-equalizer-line" onClick={() => onConfigure(school)}>
              Configurer
            </Button>
          )}
          {onToggleActive && (
            <Button
              size="sm"
              variant={school.isActive ? "ghost" : "secondary"}
              icon={school.isActive ? "ri-pause-circle-line" : "ri-play-circle-line"}
              onClick={() => onToggleActive(school)}
            >
              {school.isActive ? "Désactiver" : "Réactiver"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
